import { useMemo, useState } from 'react'

import type { ColumnDef } from '@tanstack/react-table'
import { useUnit } from 'effector-react'

import ArrowIcon from '@/assets/icons/icon-arrow.svg'
import CloseIcon from '@/assets/icons/icon-close.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { TextLabel } from '@/components/ui/text-label.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { $account } from '@/models/account.ts'
import { useCampaigns } from '@/modules/campaigns/state/hooks.ts'
import type { Creator } from '@/modules/creators/types.ts'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { useModifyLicenseContext } from '@/modules/licenses/components/modify-license-context.tsx'
import type { PotentialUpgrade } from '@/modules/licenses/utils.ts'
import { getPotentialUpgradesByCreator } from '@/modules/licenses/utils.ts'
import { classNames } from '@/utils/helpers.ts'

import type { LicensesTableType } from '../../types'
import { ConnectCreatorDialog } from '../dialogs/connect-creator-dialog.tsx'
import { UpgradeLicenseDialog } from '../dialogs/upgrade-license-dialog.tsx'

function isUpgradable(item: PotentialUpgrade, creator?: Creator) {
  const currentPlan = creator?.plan.split('_')[0]
  const newPlan = item.plan.displayName

  return (
    currentPlan?.toLowerCase().includes('pro') ||
    (currentPlan?.length || 1) < newPlan.length
  )
}

export const licensesTableColumns: ColumnDef<LicensesTableType | undefined>[] =
  [
    {
      accessorKey: 'planDisplayName',
      header: 'License',
      sortingFn: (left, right) => {
        return (
          (left.original?.planLevel ?? 0) - (right.original?.planLevel ?? 0)
        )
      },
      cell: ({ row }) => {
        const planDisplayName = row.original?.planDisplayName
        const planVariantName = row.original?.planVariantName

        return (
          <>
            {planDisplayName} ({planVariantName})
          </>
        )
      },
    },

    {
      accessorKey: 'creator',
      header: 'Connected Creator',
      sortingFn: (left, right) => {
        const leftCreatorName = left.original?.creator?.name ?? ''
        const rightCreatorName = right.original?.creator?.name ?? ''

        return leftCreatorName.localeCompare(rightCreatorName)
      },
      cell: ({ row }) => <ConnectedCreatorCell type={row.original} />,
    },
    {
      accessorKey: 'action',
      enableSorting: false,
      header: () => {
        return (
          <TextLabel className="w-full text-right text-sm">Action</TextLabel>
        )
      },
      cell: function Cell({ row, table }) {
        const { activeCampaign } = useCampaigns()
        const account = useUnit($account)
        const inTrial = account != null && account.inTrial
        const [open, setOpen] = useState(false)
        const [selectedPotentialUpgrade, setSelectedPotentialUpgrade] =
          useState<PotentialUpgrade | null>(null)
        const { plans, licenses } = useModifyLicenseContext()
        const isCreatorConnected = row.original?.isCreatorConnected ?? false
        const creator = row.original?.creator
        const disabled = row.original?.disabled ?? false
        const potentialUpgrades = useMemo(
          () =>
            creator
              ? getPotentialUpgradesByCreator(creator, licenses ?? [], plans)
              : [],
          [plans, licenses, creator],
        )
        const buttonDisabled =
          disabled || !isCreatorConnected || potentialUpgrades.length === 0

        const badgeClasses =
          'inline-flex items-center rounded-full px-3 font-normal bg-primary-500/10 text-primary-500 h-6 text-[0.7rem]'

        const isMonthly = row.original?.planVariantName
          ?.toLowerCase()
          .includes('monthly')

        const shouldPulse = !!activeCampaign && isMonthly && !buttonDisabled

        const upgradeButton = useMemo(() => {
          if (!isCreatorConnected || inTrial) {
            return (
              <Tooltip
                content={
                  !isCreatorConnected
                    ? 'Connect this license to a creator to upgrade'
                    : 'Please end your trial to upgrade this license.'
                }
              >
                <Button
                  as="div"
                  variant="ghost"
                  className="px-1 text-secondary-300"
                >
                  Upgrade License
                  <ArrowIcon className="rotate-90" />
                </Button>
              </Tooltip>
            )
          }

          return (
            <PopoverTrigger>
              <Button
                as="div"
                disabled={buttonDisabled}
                variant="ghost"
                className={classNames(
                  `px-1 hover:text-primary-600 disabled:text-secondary-300`,
                  { 'animate-scale-up': shouldPulse },
                )}
              >
                Upgrade License
                <ArrowIcon className="rotate-90" />
              </Button>
            </PopoverTrigger>
          )
        }, [buttonDisabled, inTrial, isCreatorConnected, shouldPulse])

        const upgrades = useMemo(() => {
          return potentialUpgrades
            .filter((item) => {
              if (item.interval === 'monthly') {
                return true
              }

              return !potentialUpgrades.find(
                (upgrade) =>
                  upgrade.plan === item.plan && upgrade.interval === 'monthly',
              )
            })
            .filter((item) => isUpgradable(item, creator))
            .map((item) => (
              <Button
                key={`${item.interval}-${item.plan.name}`}
                className="w-full px-4 py-2 text-title hover:bg-background"
                variant="ghost"
                onClick={() => {
                  setSelectedPotentialUpgrade(item)
                  setOpen(true)
                }}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  {item.plan.displayName}
                  <div className={badgeClasses}>Upgrade</div>
                </div>
              </Button>
            ))
        }, [creator, potentialUpgrades])

        return (
          <>
            <div className="flex justify-end">
              <Popover>
                {upgradeButton}
                <PopoverContent className="rounded-xl p-2">
                  {upgrades}
                </PopoverContent>
              </Popover>
            </div>
            {creator && selectedPotentialUpgrade && (
              <UpgradeLicenseDialog
                key={`${selectedPotentialUpgrade.plan.name}-${selectedPotentialUpgrade.interval}`}
                open={open}
                setOpen={setOpen}
                creator={creator}
                clickedUpgrade={selectedPotentialUpgrade}
                potentialUpgrades={potentialUpgrades}
              />
            )}
          </>
        )
      },
    },
  ]

const ConnectedCreatorCell = ({ type }: { type?: LicensesTableType }) => {
  const isCreatorConnected = type?.isCreatorConnected ?? false

  if (!isCreatorConnected) {
    return (
      <ConnectCreatorCell
        planDisplayName={type?.planDisplayName ?? '-'}
        licenseId={type?.licenseId ?? -1}
      />
    )
  }

  const creator = type?.creator

  return (
    <div className="flex items-center gap-3">
      <CreatorAvatar
        size={40}
        name={creator?.name ?? '-'}
        avatarSrc={creator?.avatar.croppedUrl ?? ''}
      />
      <TextLabel>{creator?.name ?? '-'}</TextLabel>
    </div>
  )
}

interface ConnectCreatorCellProps {
  planDisplayName: string
  licenseId: number
}

const ConnectCreatorCell = (props: ConnectCreatorCellProps) => {
  const { planDisplayName, licenseId } = props
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        className="flex items-center gap-3 px-0 "
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-dashed border-primary-500 hover:border-solid">
          <CloseIcon className="rotate-45 text-primary-500" />
        </div>
        <p className="text-sm font-medium text-primary-500">Connect Creator</p>
      </Button>
      <ConnectCreatorDialog
        key={licenseId}
        open={open}
        setOpen={setOpen}
        planDisplayName={planDisplayName}
        licenseId={licenseId}
      />
    </>
  )
}
